import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { authActions, authSelectors } from '../modules/auth';
import PasswordResetHandler from './handlers/PasswordResetHandler';
import VerifyEmailHandler from './handlers/VerifyEmailHandler';
import RecoverEmailHandler from './handlers/RecoverEmailHandler';
import EmailActionErrorBoundary from './EmailActionErrorBoundary';
import PageHeader from '../../components/PageHeader';

function isSafeRedirectUrl(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.origin === window.location.origin;
    } catch {
        return false;
    }
}

/**
 * EmailActionPage - Central handler for Firebase Out-of-Band (OOB) email actions
 *
 * This component reads the OOB action type from query parameters and routes to
 * the appropriate handler component. Supports custom styling and error messaging
 * to match FNI's central auth application.
 *
 * Supported modes:
 * - resetPassword: Custom password reset with dual entry validation
 * - verifyEmail: Email verification handler
 * - recoverEmail: Email recovery handler
 */
export default function EmailActionPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tenantCode = useSelector(authSelectors.currentSelectedTenant);

    const [mode, setMode] = useState(null);
    const [oobCode, setOobCode] = useState(null);
    const [apiKey, setApiKey] = useState(null);
    const [continueUrl, setContinueUrl] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Parse query parameters from the Firebase email link
        const searchParams = new URLSearchParams(location.search);
        const actionMode = searchParams.get('mode');
        const actionCode = searchParams.get('oobCode');
        const actionApiKey = searchParams.get('apiKey');
        const actionContinueUrl = searchParams.get('continueUrl');
        const tenantCode = searchParams.get('tid');

        if (tenantCode) {
            dispatch(authActions.setCurrentTenant(tenantCode));
        }

        if (!actionMode || !actionCode) {
            navigate(tenantCode ? `/auth/login/${tenantCode}` : '/auth/login', {
                state: {
                    message: "Logging out, no action code found...",
                    type: 'error'
                }
            });
            return;
        }

        setMode(actionMode);
        setOobCode(actionCode);
        setApiKey(actionApiKey);
        setContinueUrl(actionContinueUrl);
    }, [location.search]);

    const handleSuccess = (message) => {
        // Set success message in Redux and redirect to login
        dispatch(authActions.setAlert({
            type: 'success',
            message: message
        }));

        if (continueUrl && isSafeRedirectUrl(continueUrl)) {
            window.location.href = continueUrl;
            return;
        }

        // Navigate to login page with success message
        navigate(tenantCode ? `/auth/login/${tenantCode}` : '/auth/login', {
            state: {
                message: message,
                type: 'success'
            }
        });
    };

    const handleError = (errorMessage) => {
        // Set error state in Redux and redirect to login
        dispatch(authActions.setAlert({
            type: 'error',
            message: errorMessage
        }));

        if (continueUrl && isSafeRedirectUrl(continueUrl)) {
            window.location.href = continueUrl;
            return;
        }

        // Navigate to login page with error message
        navigate(tenantCode ? `/auth/login/${tenantCode}` : '/auth/login', {
            state: {
                message: errorMessage,
                type: 'error'
            }
        });
    };

    const handleCancel = () => {
        if (continueUrl && isSafeRedirectUrl(continueUrl)) {
            window.location.href = continueUrl;
            return;
        }

        // Handle cancel action without showing error message
        navigate(tenantCode ? `/auth/login/${tenantCode}` : '/auth/login');
    };

    const styles = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
    };

    if (error) {
        return (
            <Container sx={styles} maxWidth='md'>
                <PageHeader text='Email Action Error' variant='h4' />
                <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!mode || !oobCode) {
        return (
            <Container sx={styles} maxWidth='md'>
                <PageHeader text='Loading...' variant='h4' />
            </Container>
        );
    }

    // Route to appropriate handler based on mode
    const renderHandler = () => {
        switch (mode) {
            case 'resetPassword':
                return (
                    <PasswordResetHandler
                        oobCode={oobCode}
                        apiKey={apiKey}
                        continueUrl={continueUrl}
                        tenantCode={tenantCode}
                        onSuccess={handleSuccess}
                        onError={handleError}
                        onCancel={handleCancel}
                    />
                );

            case 'verifyEmail':
                return (
                    <VerifyEmailHandler
                        oobCode={oobCode}
                        apiKey={apiKey}
                        continueUrl={continueUrl}
                        onSuccess={handleSuccess}
                        onError={handleError}
                    />
                );

            case 'recoverEmail':
                return (
                    <RecoverEmailHandler
                        oobCode={oobCode}
                        onSuccess={handleSuccess}
                        onError={handleError}
                    />
                );

            default:
                return (
                    <Container sx={styles} maxWidth='md'>
                        <PageHeader text='Unsupported Action' variant='h4' />
                        <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                            The email action '{mode}' is not yet implemented.
                        </Alert>
                    </Container>
                );
        }
    };

    return (
        <EmailActionErrorBoundary>
            {renderHandler()}
        </EmailActionErrorBoundary>
    );
}
